import type {
  NormalizationType,
  OCGCurveType,
  OCGTerritory,
} from './data-typing';

import { savitzkyGolaySmooth } from '@/src/store/calculators/OCGCalculatorStore';

export class BiomarkerOCGCurve {
  private biomarkerName: OCGCurveType;
  private territory: OCGTerritory;
  private normalization: NormalizationType = 'none';
  private data: number[];
  private sliceIndex: string;
  private interpolatedIndexes: number[] | null = null;

  private availOperations = [
    'min',
    'max',
    'median',
    'mean',
    // 'TTP',
    'AUC',
    // 'FWHM',
  ];

  private operations: Record<
    (typeof this.availOperations)[number],
    (arr: number[]) => number
  > = {
    min: (arr: number[]) => Math.min(...arr),
    max: (arr: number[]) => Math.max(...arr),
    median: (arr: number[]) => {
      const sorted = [...arr].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    },
    mean: (arr: number[]) =>
      arr.reduce((sum, num) => sum + num, 0) / arr.length,
    // // TTP: Returns the index of the maximum value (assumes uniform time spacing)
    // TTP: (arr: number[]) => arr.indexOf(Math.max(...arr)),
    // AUC: Trapezoidal integration (assuming unit spacing between indices)
    AUC: (arr: number[]) => {
      if (arr.length < 2) return arr.length ? arr[0] : 0;
      let auc = 0;
      for (let i = 1; i < arr.length; i++) {
        auc += (arr[i] + arr[i - 1]) / 2;
      }
      return auc;
    },
    // FWHM: Calculates the width (in index units) where the value is at least half of the maximum
    FWHM: (arr: number[]) => {
      const maxVal = Math.max(...arr);
      const halfMax = maxVal / 2;
      let left = -1;
      let right = -1;
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] >= halfMax) {
          left = i;
          break;
        }
      }
      for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i] >= halfMax) {
          right = i;
          break;
        }
      }
      if (left === -1 || right === -1) return 0;
      return right - left;
    },
  };

  constructor(
    biomarkerName: OCGCurveType,
    territory: OCGTerritory,
    normalization: NormalizationType,
    data: number[],
    sliceIndex: string,
    interpolatedIndexes: number[] | null = null,
  ) {
    this.biomarkerName = biomarkerName;
    this.territory = territory;
    this.normalization = normalization || 'none';
    this.data = data;
    this.interpolatedIndexes = interpolatedIndexes;
    this.sliceIndex = sliceIndex;
  }

  public getBiomarkerName(): OCGCurveType {
    return this.biomarkerName;
  }

  public getBiomarkerNameWithTerritory(): string {
    return `${this.biomarkerName} (${this.territory})`;
  }

  public getTerritory(): OCGTerritory {
    return this.territory;
  }

  public getData(): number[] {
    return this.data;
  }

  public getSmoothData(smoothType = 'svGol'): number[] {
    if (smoothType !== 'svGol') {
      return [...this.data];
    }

    return this.getSavitzkyGolayData();
  }

  private getSavitzkyGolayData(): number[] {
    return savitzkyGolaySmooth(this.data, 5, 2);
  }

  public getSliceIndex(): string {
    return this.sliceIndex;
  }

  public getInterpolatedIndexes(): number[] | null {
    return this.interpolatedIndexes;
  }

  public getNormalization(): NormalizationType {
    return this.normalization;
  }

  public getOperation(operation: string) {
    if (!this.availOperations.includes(operation)) {
      throw new Error(
        `Invalid operation. Available operations are: ${this.availOperations.join(
          ', ',
        )}`,
      );
    }
    return this.operations[operation](this.data);
  }

  public getNormalizedData(): number[] {
    const min = Math.min(...this.data);
    const max = Math.max(...this.data);
    return this.data.map(d => (d - min) / (max - min));
  }

  public getStructuredDataForTable() {
    /*
    Template:
     {
      title: this.getBiomarkerNameWithTerritory(),
      headers: [Min, Max],
      rows: [
        [5,6],
      ],
    },
    */
    const structuredData = {
      title: this.getBiomarkerNameWithTerritory(),
      headers: this.availOperations.map(
        operation => operation.charAt(0).toUpperCase() + operation.slice(1),
      ),
      rows: [
        this.availOperations.map(operation =>
          Number(this.getOperation(operation).toFixed(2)),
        ),
      ],
    };
    return structuredData;
  }
}
